/**
 * Spare parts search.
 *
 * The customer types an article number or a model name, picks a product from the
 * dropdown, and the spare parts assigned to that product are rendered in place. The
 * product they picked is only ever used to look the spare parts up: it is never linked
 * to, never rendered as a card, and selecting it never navigates anywhere.
 *
 * Three sections do the Liquid side of the work, all fetched through the Section
 * Rendering API so metafields, prices, stock and market context stay correct:
 *   spare-parts-suggest  dropdown entries, already filtered to products that have parts
 *   spare-parts-results  the parts of one product, plus any article numbers left to resolve
 *   spare-parts-card     one article number resolved to a card by exact SKU match
 *
 * The two search-backed lookups go through /search rather than /search/suggest, because
 * predictive search does not index variant SKUs and so finds nothing for an article
 * number like 108661.
 *
 * A fourth section, spare-parts-index, covers lamps that are no longer active in Shopify.
 * Search cannot return a product that is not published, so those lamps are rebuilt from
 * the spare parts instead, which are still active and already record the article numbers
 * they fit. spare-parts-single-card then renders those parts straight from their handles.
 */
class SparePartsSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('[data-input]');
    this.suggestions = this.querySelector('[data-suggestions]');
    this.results = this.querySelector('[data-results]');
    this.resultsHeading = this.querySelector('[data-results-heading]');
    this.grid = this.querySelector('[data-grid]');
    this.empty = this.querySelector('[data-empty]');
    this.status = this.querySelector('[data-status]');

    this.minCharacters = parseInt(this.dataset.minCharacters, 10) || 2;
    this.headingTemplate = this.dataset.resultsHeading || 'Spare parts for [product]';
    this.searchingText = this.dataset.searchingText || '';

    this.items = [];
    this.activeIndex = -1;
    this.term = '';
    this.suggestController = null;
    this.partsController = null;

    this.index = null;
    this.indexPromise = null;
    this.partTypes = new Set();
    this.articleLookups = new Map();
    // v3 added the part product types. Bumping the key discards indexes cached before
    // that, which carry no types and so could not filter the dropdown.
    this.indexKey = `spare-parts-index:v3:${this.rootUrl}`;

    this.onInput = this.debounce(this.search.bind(this), 250);
  }

  connectedCallback() {
    this.input.addEventListener('input', () => {
      this.toggleReset();
      this.onInput();
    });
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.addEventListener('focus', () => {
      // Warmed on first interaction rather than page load, so a visitor who never
      // searches pays nothing for it.
      this.ensureIndex();
      if (this.items.length) this.openSuggestions();
    });

    this.addEventListener('focusout', () => {
      // Let focus land on its new target before deciding the dropdown lost it.
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.closeSuggestions();
      });
    });

    this.querySelectorAll('[data-reset]').forEach((button) => {
      button.addEventListener('click', this.reset.bind(this));
    });

    this.restoreFromUrl();
  }

  /* ------------------------------------------------------------------ helpers */

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  get rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * `options[prefix]=last` gives the last word prefix matching, so "styrk" still hits.
   * `unavailable_products=show` is explicit because the shop default can hide anything
   * out of stock, which would drop a lamp from the dropdown and, worse, stop a spare part
   * card resolving. Spare parts are exactly what a customer needs to see when the item is
   * out of stock, so availability must not decide whether it can be found.
   */
  searchUrl(term, sectionId) {
    return (
      `${this.dataset.searchUrl}?q=${encodeURIComponent(term)}` +
      `&type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=show` +
      `&section_id=${sectionId}`
    );
  }

  static escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (character) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  /** Escapes first, then marks the matched run, so product data can never inject markup. */
  static highlight(value, term) {
    const escaped = SparePartsSearch.escapeHtml(value);
    if (!term) return escaped;

    // Both sides have to be measured after escaping, or a term containing something like
    // & would slice at the wrong offset.
    const escapedTerm = SparePartsSearch.escapeHtml(term);
    const index = escaped.toLowerCase().indexOf(escapedTerm.toLowerCase());
    if (index === -1) return escaped;

    const end = index + escapedTerm.length;
    return `${escaped.slice(0, index)}<mark>${escaped.slice(index, end)}</mark>${escaped.slice(end)}`;
  }

  async fetchDocument(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Spare parts search: ${response.status} for ${url}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /** Visually hidden live region, so screen readers hear that the parts have landed. */
  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  toggleReset() {
    this.querySelectorAll('.spare-parts-search__reset').forEach((button) => {
      button.classList.toggle('hidden', this.input.value.length === 0);
    });
  }


  /* ---------------------------------------------------------------- part index */

  /**
   * Reverse index: article number -> the spare parts that fit it. Built from the parts
   * collections (spare parts and spare bulbs), which are the only place a discontinued lamp
   * still exists, since an inactive product is absent from search entirely.
   *
   * Roughly 25 KB for ~205 parts, so it is fetched once and kept in localStorage. A
   * failure here is never fatal: the search-backed path keeps working without it.
   */
  ensureIndex() {
    if (!this.dataset.indexSection) return Promise.resolve(null);
    if (this.indexPromise) return this.indexPromise;

    this.indexPromise = this.buildIndex().catch((error) => {
      console.error(error);
      return null;
    });
    return this.indexPromise;
  }

  async buildIndex() {
    const cached = this.readCachedIndex();
    const data = cached || (await this.fetchIndexRows());
    if (!cached && data.rows.length) this.writeCachedIndex(data);

    const { rows } = data;
    this.partTypes = new Set((data.types || []).map((type) => type.trim().toLowerCase()).filter(Boolean));

    const byNumber = new Map();
    const seen = new Set();
    rows.forEach((row) => {
      // A part can sit in more than one of the collections; index it once or it would
      // render as a duplicate card.
      if (seen.has(row.h)) return;
      seen.add(row.h);

      const family = SparePartsSearch.lampFamily(row.f);
      (row.n || []).forEach((number) => {
        if (!byNumber.has(number)) byNumber.set(number, { number, family, parts: [] });
        const entry = byNumber.get(number);
        entry.parts.push({ handle: row.h, title: row.t, sku: row.s });
        if (!entry.family && family) entry.family = family;
      });
    });

    this.index = byNumber;
    return byNumber;
  }

  get indexUrls() {
    try {
      const urls = JSON.parse(this.dataset.indexUrls || '[]');
      return Array.isArray(urls) ? urls.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  async fetchIndexRows() {
    const collections = await Promise.all(
      this.indexUrls.map((base) =>
        this.fetchCollectionRows(base).catch((error) => {
          // One unreachable collection should not take the others down with it.
          console.error(error);
          return { rows: [], types: [] };
        })
      )
    );
    return {
      rows: [].concat(...collections.map((c) => c.rows)),
      types: [...new Set([].concat(...collections.map((c) => c.types)))],
    };
  }

  async fetchCollectionRows(base) {
    const url = (page) => `${base}?section_id=${this.dataset.indexSection}&page=${page}`;

    const read = (doc) => {
      const node = doc.querySelector('[data-spare-parts-index]');
      if (!node) return { rows: [], pages: 1, types: [] };
      return {
        rows: JSON.parse(node.textContent),
        pages: parseInt(node.dataset.pages, 10) || 1,
        types: (node.dataset.partTypes || '').split('|').filter(Boolean),
      };
    };

    const first = read(await this.fetchDocument(url(1)));
    let rows = first.rows;
    let types = first.types;

    if (first.pages > 1) {
      const rest = await Promise.all(
        Array.from({ length: first.pages - 1 }, (_, i) =>
          this.fetchDocument(url(i + 2))
            .then((doc) => read(doc))
            .catch(() => ({ rows: [], types: [] }))
        )
      );
      rows = rows.concat(...rest.map((r) => r.rows));
      types = types.concat(...rest.map((r) => r.types));
    }

    return { rows, types: [...new Set(types)] };
  }

  readCachedIndex() {
    try {
      const raw = window.localStorage.getItem(this.indexKey);
      if (!raw) return null;
      const { at, rows, types } = JSON.parse(raw);
      // A day is short enough that a newly published spare part shows up quickly, and
      // long enough that repeat visitors never refetch.
      if (!at || Date.now() - at > 86400000) return null;
      return Array.isArray(rows) ? { rows, types: types || [] } : null;
    } catch (error) {
      return null;
    }
  }

  writeCachedIndex({ rows, types }) {
    try {
      window.localStorage.setItem(this.indexKey, JSON.stringify({ at: Date.now(), rows, types }));
    } catch (error) {
      // Private browsing or a full quota. The index still works for this page view.
    }
  }

  /**
   * Entries for article numbers the search could not return a product for, which is what
   * a discontinued lamp looks like. Anything already covered by an active suggestion is
   * skipped so the same lamp never appears twice.
   */
  indexSuggestions(term, activeItems) {
    if (!this.index) return [];

    const folded = SparePartsSearch.fold(term);
    // Article numbers can contain a comma, as in Svaneholm 8004,100.
    const numeric = /^[\d,]+$/.test(folded);
    const taken = new Set(activeItems.map((item) => String(item.sku || '')).filter(Boolean));

    const matches = [];
    this.index.forEach((entry) => {
      if (taken.has(entry.number)) return;

      const hit = numeric
        ? entry.number.startsWith(folded)
        : SparePartsSearch.fold(entry.family).includes(folded);
      if (hit) matches.push(entry);
    });

    matches.sort((a, b) => a.number.localeCompare(b.number));
    return matches.slice(0, 5).map((entry) => {
      // A spare part often fits several families at once ("QUATTRO,BUDDY,STREAM,ZENIT"),
      // and which one this article number belongs to is not recoverable. Naming the lamp
      // is only honest when the part points at a single family; otherwise the article
      // number leads and the families stay below it as context.
      const families = entry.family
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean);
      const single = families.length === 1 ? families[0] : '';

      return {
        type: 'article',
        number: entry.number,
        family: entry.family,
        parts: entry.parts,
        title: single ? `${SparePartsSearch.titleCase(single)} ${entry.number}` : entry.number,
        meta: single ? [] : families.map((name) => SparePartsSearch.titleCase(name)),
        sku: entry.number,
      };
    });
  }

  /**
   * A part's family usually names the lamps it fits (MÄSTER on Mäster Spare Glass), which
   * is what lets a discontinued lamp be shown as "Mäster 703519". Generic parts carry a
   * family describing the part instead: SPAREBULB, UNIVERSAL, UNIVERSAL TRANSFORMER,
   * SPARE PART and SPARE GLASS, 38 of the 206 parts. Naming a lamp after those would read
   * "Sparebulb 705334" and make "universal" suggest unrelated lamps, so they are dropped.
   */
  static lampFamily(value) {
    return String(value || '')
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name && !/^(spare|universal)/i.test(name))
      .join(', ');
  }

  /**
   * True for a product whose type matches one of the parts in the parts collections, in
   * whatever language the current market renders. Spare parts are already filtered out in
   * Liquid by the collection they sit in, but some are in no collection at all: 6 spare
   * bulbs, among them Origo Spare Bulb and Modu Spare Bulb, are only recognisable by type.
   */
  isPartType(type) {
    if (!this.partTypes.size) return false;
    return this.partTypes.has(String(type || '').trim().toLowerCase());
  }

  static fold(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/å|ä/g, 'a')
      .replace(/ö|ø/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/é/g, 'e')
      .replace(/ß/g, 'ss');
  }

  static titleCase(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/(^|[\s/-])([^\s/-])/g, (match, boundary, letter) => boundary + letter.toUpperCase());
  }

  /* --------------------------------------------------------------- suggestions */

  async search() {
    const term = this.input.value.trim();
    this.term = term;

    if (term.length < this.minCharacters) {
      this.items = [];
      this.closeSuggestions();
      this.hideEmpty();
      return;
    }

    if (this.suggestController) this.suggestController.abort();
    this.suggestController = new AbortController();

    this.renderSuggestionMessage(this.searchingText);
    this.openSuggestions();

    const url = this.searchUrl(term, this.dataset.suggestSection);

    let products = [];
    try {
      const doc = await this.fetchDocument(url, this.suggestController.signal);
      const payload = doc.querySelector('[data-spare-parts-suggestions]');
      products = payload ? JSON.parse(payload.textContent) : [];
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      products = [];
    }

    // A later keystroke already superseded this response.
    if (term !== this.input.value.trim()) return;

    // Active products first, then any article number only the index knows about.
    await this.ensureIndex();
    if (term !== this.input.value.trim()) return;

    products = products.filter((product) => !this.isPartType(product.type));
    this.items = products.concat(this.indexSuggestions(term, products));
    this.activeIndex = -1;

    if (!this.items.length) {
      this.closeSuggestions();
      this.showEmpty();
      return;
    }

    this.hideEmpty();
    this.renderSuggestions();

    // Not awaited: the dropdown is already usable, and any row that turns out to be a
    // live product is swapped in as soon as its lookup returns.
    this.upgradeArticleEntries(term);
  }

  /**
   * An index row is only meant to stand in for a product the storefront cannot return.
   * Shopify search can miss an active product even so: Callisto Ceiling Lamp Black (108108)
   * is live, but a search for "circle" never returns it, because only the spare part that
   * fits it carries the family CIRCLE, CALLISTO. That left the lamp showing as a bare
   * article number next to properly titled rows. Looking each number up by its exact SKU
   * puts the real product back, with its title and image.
   */
  async upgradeArticleEntries(term) {
    const articles = this.items.filter((item) => item.type === 'article');
    if (!articles.length) return;

    const resolved = await Promise.all(articles.map((entry) => this.resolveArticle(entry.number)));
    if (term !== this.input.value.trim()) return;

    const byNumber = new Map(articles.map((entry, i) => [entry.number, resolved[i]]));
    const seen = new Set(this.items.filter((i) => i.type !== 'article').map((i) => i.sku));
    const merged = [];

    this.items.forEach((item) => {
      if (item.type !== 'article') {
        merged.push(item);
        return;
      }
      const product = byNumber.get(item.number);
      if (!product) {
        merged.push(item);
        return;
      }
      if (seen.has(product.sku)) return;
      seen.add(product.sku);
      merged.push(product);
    });

    if (merged.length === this.items.length && merged.every((item, i) => item === this.items[i])) return;

    this.items = merged;
    this.activeIndex = -1;
    this.renderSuggestions();
  }

  /** Exact SKU match only, cached for the session so repeated keystrokes cost nothing. */
  async resolveArticle(number) {
    if (this.articleLookups.has(number)) return this.articleLookups.get(number);

    const lookup = this.fetchDocument(this.searchUrl(number, this.dataset.suggestSection))
      .then((doc) => {
        const payload = doc.querySelector('[data-spare-parts-suggestions]');
        const rows = payload ? JSON.parse(payload.textContent) : [];
        const match = rows.find((row) => String(row.sku) === String(number)) || null;
        return match && this.isPartType(match.type) ? null : match;
      })
      .catch(() => null);

    this.articleLookups.set(number, lookup);
    return lookup;
  }

  renderSuggestionMessage(message) {
    if (!message) {
      this.suggestions.innerHTML = '';
      return;
    }
    this.suggestions.innerHTML =
      `<p class="spare-parts-search__suggestions-message">${SparePartsSearch.escapeHtml(message)}</p>`;
  }

  renderSuggestions() {
    const term = this.term;

    this.suggestions.innerHTML = this.items
      .map((item, index) => {
        const meta =
          item.type === 'article' ? item.meta : [item.sku, item.family].filter(Boolean);
        const image = item.image
          ? `<img class="spare-parts-search__suggestion-image" src="${SparePartsSearch.escapeHtml(
              item.image
            )}" alt="" width="48" height="48" loading="lazy">`
          : '<span class="spare-parts-search__suggestion-image spare-parts-search__suggestion-image--empty"></span>';

        return `
          <button
            type="button"
            class="spare-parts-search__suggestion"
            role="option"
            aria-selected="false"
            id="spare-parts-suggestion-${index}"
            data-index="${index}"
            tabindex="-1"
          >
            ${image}
            <span class="spare-parts-search__suggestion-text">
              <span class="spare-parts-search__suggestion-title">${SparePartsSearch.highlight(
                item.title,
                term
              )}</span>
              ${
                meta.length
                  ? `<span class="spare-parts-search__suggestion-meta">${meta
                      .map((value) => SparePartsSearch.highlight(value, term))
                      .join(' &middot; ')}</span>`
                  : ''
              }
            </span>
          </button>
        `;
      })
      .join('');

    this.suggestions.querySelectorAll('[data-index]').forEach((button) => {
      button.addEventListener('click', () => {
        this.select(this.items[parseInt(button.dataset.index, 10)]);
      });
    });

    this.openSuggestions();
  }

  openSuggestions() {
    this.suggestions.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  closeSuggestions() {
    this.suggestions.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  setActive(index) {
    const buttons = Array.from(this.suggestions.querySelectorAll('[data-index]'));
    if (!buttons.length) return;

    this.activeIndex = (index + buttons.length) % buttons.length;

    buttons.forEach((button, position) => {
      const active = position === this.activeIndex;
      button.setAttribute('aria-selected', String(active));
      button.classList.toggle('is-active', active);
      if (active) {
        this.input.setAttribute('aria-activedescendant', button.id);
        button.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        if (this.suggestions.hidden && this.items.length) this.openSuggestions();
        this.setActive(this.activeIndex + 1);
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.setActive(this.activeIndex - 1);
        event.preventDefault();
        break;
      case 'Enter':
        // Only an actively highlighted suggestion resolves to spare parts. Typed text on
        // its own must not, and Enter must never submit or navigate.
        event.preventDefault();
        if (this.activeIndex > -1 && this.items[this.activeIndex]) {
          this.select(this.items[this.activeIndex]);
        }
        break;
      case 'Escape':
        this.closeSuggestions();
        break;
      default:
        break;
    }
  }

  /* ------------------------------------------------------------------- results */

  async select(item) {
    if (!item) return;

    this.input.value = item.title;
    this.toggleReset();
    this.closeSuggestions();
    this.hideEmpty();

    if (item.type === 'article') {
      await this.showPartsForArticle(item.number, item.parts, item.title);
      this.setUrlParam('article', item.number);
      return;
    }

    await this.showPartsFor(item.handle, item.title);
    this.setUrlParam('product', item.handle);
  }

  /**
   * A lamp with no active product behind it. There is no metafield left to read, so the
   * parts come from the index, which already holds their handles: each one is rendered
   * straight from its handle instead of being resolved through search.
   */
  async showPartsForArticle(number, parts, title) {
    if (this.partsController) this.partsController.abort();
    this.partsController = new AbortController();
    const { signal } = this.partsController;

    let known = parts;
    if (!known) {
      await this.ensureIndex();
      const entry = this.index && this.index.get(String(number));
      known = entry ? entry.parts : [];
      if (entry && !title) {
        title = entry.family
          ? `${SparePartsSearch.titleCase(entry.family)} ${number}`
          : String(number);
        this.input.value = title;
        this.toggleReset();
      }
    }

    this.results.hidden = false;
    this.grid.innerHTML = '';
    this.setHeading(title || String(number));
    this.classList.add('is-loading');

    await this.renderPartCards(known, signal);

    this.classList.remove('is-loading');
    this.finishResults();
  }

  /**
   * snippets/price.liquid only prints the regular price `{% unless template contains
   * 'product' %}`, so a card rendered against the product route comes back with its price
   * missing. These parts are therefore rendered through the search route by exact SKU,
   * the same path the active products already use. The product route stays as a fallback
   * for a part with no SKU, which is the only case where a handle is all we have.
   */
  async renderPartCards(parts, signal) {
    const placeholders = parts.map(() => {
      const li = document.createElement('li');
      li.className = 'grid__item spare-parts-search__placeholder';
      this.grid.appendChild(li);
      return li;
    });

    await Promise.all(
      parts.map(async (part, index) => {
        const url = part.sku
          ? this.searchUrl(part.sku, this.dataset.cardSection)
          : `${this.rootUrl}products/${encodeURIComponent(part.handle)}` +
            `?section_id=${this.dataset.singleCardSection}`;
        try {
          const doc = await this.fetchDocument(url, signal);
          const card = doc.querySelector('[data-spare-parts-card] .grid__item');
          if (card) placeholders[index].replaceWith(card);
          else placeholders[index].remove();
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
          placeholders[index].remove();
        }
      })
    );
  }

  async showPartsFor(handle, title) {
    if (this.partsController) this.partsController.abort();
    this.partsController = new AbortController();
    const { signal } = this.partsController;

    this.results.hidden = false;
    this.grid.innerHTML = '';
    this.setHeading(title);
    this.classList.add('is-loading');

    let pending = [];
    try {
      const doc = await this.fetchDocument(
        `${this.rootUrl}products/${encodeURIComponent(handle)}?section_id=${this.dataset.resultsSection}`,
        signal
      );

      const container = doc.querySelector('[data-spare-parts-results]');
      if (!container) throw new Error('Spare parts search: results section returned nothing');

      if (!title) {
        // Restored from a shared link, so the product name only arrives with the results.
        this.setHeading(container.dataset.title);
        this.input.value = container.dataset.title || '';
        this.toggleReset();
      }

      const items = container.querySelector('[data-spare-parts-items]');
      if (items) this.grid.innerHTML = items.innerHTML;

      const payload = container.querySelector('[data-spare-parts-pending]');
      pending = payload ? JSON.parse(payload.textContent) : [];
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
    }

    if (pending.length) await this.resolveArticleNumbers(pending, signal);

    this.classList.remove('is-loading');
    this.finishResults();
  }

  /**
   * Cards are rendered by the shared card-product snippet, which prints a plain "sold
   * out" fallback and relies on the theme correcting it client-side from
   * data-next-available-date. That correction runs once on DOMContentLoaded, long before
   * these cards exist, so it has to be re-run over the injected ones.
   */
  syncCardStatus() {
    if (typeof window.updateStockStatus === 'function') {
      try {
        window.updateStockStatus();
      } catch (error) {
        console.error(error);
      }
    }

    const notifyLabel = this.dataset.notifyLabel;
    if (!notifyLabel) return;

    this.grid.querySelectorAll('.card-wrapper').forEach((card) => {
      if (card.dataset.proInStock === 'true') return;

      const restock = card.dataset.nextAvailableDate;
      if (!restock) return;

      const restockDate = new Date(restock);
      if (Number.isNaN(restockDate.valueOf()) || restockDate <= new Date()) return;

      const button = card.querySelector('.quick-add__submit');
      const link = card.querySelector('a.full-unstyled-link');
      if (!button || !link) return;

      // The snippet only ever labels this "sold out, no planned restock", which flatly
      // contradicts the restock line right above it. The product page owns the notify
      // flow, so hand the customer over to it instead of restating it here.
      const notify = document.createElement('a');
      notify.className = button.className;
      notify.href = link.getAttribute('href');
      notify.textContent = notifyLabel;

      const form = button.closest('product-form');
      (form || button).replaceWith(notify);
    });
  }

  finishResults() {
    this.syncCardStatus();

    if (!this.grid.children.length) {
      this.results.hidden = true;
      this.showEmpty();
      return;
    }

    this.announce(this.resultsHeading.textContent);
    this.resultsHeading.focus({ preventScroll: true });
    this.results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * The Spare Parts metafield holds article numbers, and Liquid can only address products
   * by handle, so each number is resolved through search and matched on an exact SKU.
   * Placeholders keep the cards in the order the metafield lists them while the lookups
   * run in parallel; any number that resolves to nothing simply drops out.
   */
  async resolveArticleNumbers(numbers, signal) {
    const placeholders = numbers.map(() => {
      const li = document.createElement('li');
      li.className = 'grid__item spare-parts-search__placeholder';
      this.grid.appendChild(li);
      return li;
    });

    await Promise.all(
      numbers.map(async (number, index) => {
        const url = this.searchUrl(number, this.dataset.cardSection);

        try {
          const doc = await this.fetchDocument(url, signal);
          const card = doc.querySelector('[data-spare-parts-card] .grid__item');
          if (card) placeholders[index].replaceWith(card);
          else placeholders[index].remove();
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
          placeholders[index].remove();
        }
      })
    );
  }

  setHeading(title) {
    if (!title) return;
    this.resultsHeading.textContent = this.headingTemplate.replace('[product]', title);
  }

  /* --------------------------------------------------------------- empty state */

  showEmpty() {
    this.empty.hidden = false;
    this.results.hidden = true;
  }

  hideEmpty() {
    this.empty.hidden = true;
  }

  /* ----------------------------------------------------------------- url state */

  setUrlParam(name, value) {
    const url = new URL(window.location.href);
    url.searchParams.delete('product');
    url.searchParams.delete('article');
    url.searchParams.set(name, value);
    window.history.replaceState({}, '', url);
  }

  clearUrlParams() {
    const url = new URL(window.location.href);
    url.searchParams.delete('product');
    url.searchParams.delete('article');
    window.history.replaceState({}, '', url);
  }

  /** Keeps a shared or reloaded link showing the same spare parts. */
  restoreFromUrl() {
    const params = new URL(window.location.href).searchParams;

    const handle = params.get('product');
    if (handle) {
      this.showPartsFor(handle, '');
      return;
    }

    const article = params.get('article');
    if (article) this.showPartsForArticle(article, null, '');
  }

  reset() {
    if (this.suggestController) this.suggestController.abort();
    if (this.partsController) this.partsController.abort();

    this.input.value = '';
    this.items = [];
    this.term = '';
    this.grid.innerHTML = '';
    this.results.hidden = true;

    this.toggleReset();
    this.closeSuggestions();
    this.hideEmpty();
    this.clearUrlParams();
    this.input.focus();
  }
}

customElements.define('spare-parts-search', SparePartsSearch);
